import './GuideTour.css';

import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { closeOutline, pauseOutline, playOutline } from 'ionicons/icons';
import clsx from 'clsx';
import Button from '../../Button/Button';
import MarkdownView from '../../Manager/GridInput/components/MarkdownView/MarkdownView';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useGuideContext } from '../../../context/GuideContext/GuideContext';
import { TourPlacement, TourStep } from '../../../types/guides';
import {
  findAnyGuideTarget,
  resolveGuideTarget,
  revealGuideTarget,
  waitForGuideTarget,
} from '../../../utils/guides/resolveGuideTarget';

type Phase = 'preparing' | 'ready' | 'missing';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const SPOTLIGHT_PADDING = 6;
const CARD_GAP = 12;
const VIEWPORT_MARGIN = 8;

const readBox = (element: HTMLElement): Box => {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };
};

const sameBox = (a: Box | null, b: Box | null): boolean =>
  !!a &&
  !!b &&
  Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 &&
  Math.abs(a.height - b.height) < 0.5;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max));

/** Card position next to the spotlight; falls back to the side with most room. */
const positionCard = (
  box: Box,
  cardWidth: number,
  cardHeight: number,
  placement: TourPlacement | undefined
): { top: number; left: number } => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spaceBelow = vh - (box.top + box.height);
  const spaceAbove = box.top;
  const spaceRight = vw - (box.left + box.width);
  const spaceLeft = box.left;
  let resolved: TourPlacement = placement ?? 'auto';
  if (resolved === 'auto') {
    if (spaceBelow >= cardHeight + CARD_GAP) resolved = 'bottom';
    else if (spaceAbove >= cardHeight + CARD_GAP) resolved = 'top';
    else if (spaceRight >= cardWidth + CARD_GAP) resolved = 'right';
    else if (spaceLeft >= cardWidth + CARD_GAP) resolved = 'left';
    else resolved = 'bottom';
  }
  let top: number;
  let left: number;
  switch (resolved) {
    case 'top':
      top = box.top - CARD_GAP - cardHeight;
      left = box.left;
      break;
    case 'right':
      top = box.top;
      left = box.left + box.width + CARD_GAP;
      break;
    case 'left':
      top = box.top;
      left = box.left - CARD_GAP - cardWidth;
      break;
    case 'bottom':
    default:
      top = box.top + box.height + CARD_GAP;
      left = box.left;
  }
  return {
    top: clamp(top, VIEWPORT_MARGIN, vh - cardHeight - VIEWPORT_MARGIN),
    left: clamp(left, VIEWPORT_MARGIN, vw - cardWidth - VIEWPORT_MARGIN),
  };
};

const autoplayDurationMs = (step: TourStep): number =>
  Math.min(20000, 4500 + step.body.length * 35);

// Layer C of Průvodce MySeptim: highlights real UI and narrates it. The dimmed
// area is purely visual (no pointer capture), so the user can act on the
// highlighted control while reading — the guide explains, it never submits.
const GuideTour = () => {
  const { t } = useTranslate();
  const { activeTour, nextStep, prevStep, stopTour, toggleMode, navigate } =
    useGuideContext();
  const [phase, setPhase] = useState<Phase>('preparing');
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [cardStyle, setCardStyle] = useState<CSSProperties>({});
  const cardRef = useRef<HTMLDivElement | null>(null);

  const tour = activeTour?.tour ?? null;
  const stepIndex = activeTour?.stepIndex ?? 0;
  const mode = activeTour?.mode ?? 'interactive';
  const step = tour?.steps[stepIndex] ?? null;
  const isLast = !!tour && stepIndex >= tour.steps.length - 1;

  // Resolve the step: run its navigation, wait for the anchor, then spotlight it.
  useEffect(() => {
    if (!tour || !step) return;
    let cancelled = false;
    setPhase('preparing');
    setTargetElement(null);
    setBox(null);
    const run = async () => {
      let navigated = false;
      if (step.action) navigated = navigate(step.action);
      if (step.target.kind === 'page') {
        await new Promise((resolvePromise) =>
          window.setTimeout(resolvePromise, navigated ? 350 : 0)
        );
        if (!cancelled) setPhase('ready');
        return;
      }
      let element = resolveGuideTarget(step.target);
      if (!element) {
        // A field on an inactive form tab or a menu item in a collapsed block is in the
        // DOM but hidden: open its container first, then wait for it to render.
        const hidden = findAnyGuideTarget(step.target);
        if (hidden && revealGuideTarget(hidden)) {
          element = await waitForGuideTarget(step.target, { timeoutMs: 1500 });
        }
      }
      if (!element) {
        await waitForGuideTarget(step.waitFor ?? step.target, {
          timeoutMs: step.action ? 6000 : 2500,
        });
        if (cancelled) return;
        element = resolveGuideTarget(step.target);
        if (!element) {
          const hidden = findAnyGuideTarget(step.target);
          if (hidden && revealGuideTarget(hidden)) {
            element = await waitForGuideTarget(step.target, { timeoutMs: 1500 });
          }
        }
      }
      if (cancelled) return;
      if (!element) {
        setPhase('missing');
        return;
      }
      element.scrollIntoView({ block: 'center', inline: 'nearest' });
      setTargetElement(element);
      setPhase('ready');
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tour, step, stepIndex, navigate]);

  // Follow the anchor while the page scrolls, resizes or re-renders.
  useEffect(() => {
    if (!targetElement) return;
    let frame = 0;
    const tick = () => {
      if (!targetElement.isConnected) {
        setPhase('missing');
        setTargetElement(null);
        return;
      }
      const next = readBox(targetElement);
      setBox((previous) => (sameBox(previous, next) ? previous : next));
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [targetElement]);

  // Place the card once its size is known.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const cardWidth = card.offsetWidth;
    const cardHeight = card.offsetHeight;
    if (box && phase === 'ready') {
      const { top, left } = positionCard(box, cardWidth, cardHeight, step?.placement);
      setCardStyle({ top, left });
      return;
    }
    setCardStyle({
      top: clamp((window.innerHeight - cardHeight) / 2, VIEWPORT_MARGIN, window.innerHeight),
      left: clamp((window.innerWidth - cardWidth) / 2, VIEWPORT_MARGIN, window.innerWidth),
    });
  }, [box, phase, step, stepIndex]);

  // Autoplay: advance after a reading pause proportional to the text length.
  useEffect(() => {
    if (!step || mode !== 'autoplay' || phase === 'preparing') return;
    const timer = window.setTimeout(nextStep, autoplayDurationMs(step));
    return () => window.clearTimeout(timer);
  }, [step, stepIndex, mode, phase, nextStep]);

  // Keyboard: Esc ends, arrows move.
  useEffect(() => {
    if (!tour) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        stopTour();
      } else if (event.key === 'ArrowRight') {
        nextStep();
      } else if (event.key === 'ArrowLeft') {
        prevStep();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tour, nextStep, prevStep, stopTour]);

  if (!tour || !step) return null;

  const spotlight = phase === 'ready' && box;
  const autoplayStyle = {
    animationDuration: `${autoplayDurationMs(step)}ms`,
  } as CSSProperties;

  return createPortal(
    <div className="guide-tour" role="dialog" aria-label={tour.title} data-guide-tour>
      {spotlight ? (
        <div
          className="guide-tour-spotlight"
          style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
        />
      ) : (
        <div className="guide-tour-dim" />
      )}
      <div
        ref={cardRef}
        className={clsx('guide-tour-card', phase === 'preparing' && 'guide-tour-card-preparing')}
        style={cardStyle}
      >
        <div className="guide-tour-card-head">
          <span className="guide-tour-eyebrow">
            {tour.title} · {t('guide.step')} {stepIndex + 1} {t('guide.of')} {tour.steps.length}
          </span>
          <button
            type="button"
            className="guide-tour-close"
            onClick={stopTour}
            aria-label={t('guide.end')}
            title={t('guide.end')}
          >
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        {step.title && <h3 className="guide-tour-title">{step.title}</h3>}
        {phase === 'preparing' ? (
          <p className="guide-tour-muted">{t('guide.preparing')}</p>
        ) : (
          <MarkdownView value={step.body} className="guide-tour-body" />
        )}
        {phase === 'missing' && (
          <p className="guide-tour-note">{t('guide.targetMissing')}</p>
        )}
        <div className="guide-tour-actions">
          <button
            type="button"
            className={clsx('guide-tour-mode', mode === 'autoplay' && 'guide-tour-mode-on')}
            onClick={toggleMode}
            aria-pressed={mode === 'autoplay'}
            title={mode === 'autoplay' ? t('guide.autoplayOn') : t('guide.autoplayOff')}
          >
            <IonIcon icon={mode === 'autoplay' ? pauseOutline : playOutline} />
          </button>
          <span className="guide-tour-dots" aria-hidden="true">
            {tour.steps.map((item, index) => (
              <i key={item.id} className={clsx(index <= stepIndex && 'is-on')} />
            ))}
          </span>
          <Button small fill="outline" onClick={prevStep} disabled={stepIndex === 0} round={false}>
            {t('guide.back')}
          </Button>
          <Button small fill="solid" onClick={nextStep} round={false}>
            {isLast ? t('guide.finish') : t('guide.next')}
          </Button>
        </div>
        {mode === 'autoplay' && phase !== 'preparing' && (
          <div key={`${tour.id}-${stepIndex}`} className="guide-tour-progress" style={autoplayStyle} />
        )}
      </div>
    </div>,
    document.body
  );
};

export default GuideTour;
